<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import Transcription from '../Transcription.vue';
import { ATTACHMENT_TYPES, MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const transcriptionAttachments = computed(() => {
  const supportedTypes = [
    ATTACHMENT_TYPES.AUDIO,
    ATTACHMENT_TYPES.FILE,
    ATTACHMENT_TYPES.IMAGE,
  ];
  const messageAttachments = Array.isArray(attachments.value)
    ? attachments.value
    : [];
  return messageAttachments.filter(attachment =>
    supportedTypes.includes(attachment.fileType)
  );
});

const hasAudioAttachment = computed(() => {
  return transcriptionAttachments.value.some(
    attachment => attachment.fileType === ATTACHMENT_TYPES.AUDIO
  );
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent
        v-if="renderContent && !hasAudioAttachment"
        :content="renderContent"
      />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <Transcription
        v-for="attachment in transcriptionAttachments"
        :key="`transcription-${attachment.id}`"
        :attachment="attachment"
      />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
